import React, { useState } from 'react';

/**
 * Alert - Dismissible alert message component
 *
 * @param {string} type - 'success' | 'error' | 'warning' | 'info'
 * @param {string} title - Optional title
 * @param {string} message - Alert message (or use children)
 * @param {boolean} dismissible - Show dismiss button (default: true)
 * @param {function} onDismiss - Called when dismissed
 * @param {ReactNode} children - Alternative to message prop
 */
function Alert({
  type = 'info',
  title,
  message,
  dismissible = true,
  onDismiss,
  children
}) {
  const [visible, setVisible] = useState(true);

  if (!visible) return null;

  const getIcon = () => {
    switch (type) {
      case 'success': return '✓';
      case 'error': return '✕';
      case 'warning': return '⚠';
      case 'info': return 'ℹ';
      default: return 'ℹ';
    }
  };

  const handleDismiss = () => {
    setVisible(false);
    if (onDismiss) onDismiss();
  };

  return (
    <div className={`alert alert-${type}`} role="alert">
      <span className="alert-icon" aria-hidden="true">
        {getIcon()}
      </span>
      <div className="alert-content">
        {title && <div className="alert-title">{title}</div>}
        <div className="alert-message">{message || children}</div>
      </div>
      {dismissible && (
        <button
          className="alert-dismiss"
          onClick={handleDismiss}
          aria-label="Dismiss alert"
        >
          ×
        </button>
      )}
    </div>
  );
}

// Convenience components
export function SuccessAlert(props) {
  return <Alert type="success" {...props} />;
}

export function ErrorAlert(props) {
  return <Alert type="error" {...props} />;
}

export function WarningAlert(props) {
  return <Alert type="warning" {...props} />;
}

export function InfoAlert(props) {
  return <Alert type="info" {...props} />;
}

export default Alert;
